// ────────────────────────────────────────────────────────────
// CONVERSATION STORE
// ────────────────────────────────────────────────────────────

import { create } from 'zustand';
import { conversationRepo } from '../services/persistence';
import type { ConversationMessage } from '../types';

interface ConversationState {
  byDealId: Record<string, ConversationMessage[]>;
  // Per-deal streaming buffer. Non-empty string while Claude is replying.
  streamingDraft: Record<string, string>;
  isStreaming: Record<string, boolean>;

  load: (dealId: string) => Promise<void>;
  setMessages: (dealId: string, msgs: ConversationMessage[]) => void;
  appendMessage: (msg: ConversationMessage) => Promise<void>;
  appendMessageLocal: (msg: ConversationMessage) => void;   // no DB write — used when stream already persisted server-side
  setStreaming: (dealId: string, on: boolean) => void;
  appendStreamingDelta: (dealId: string, delta: string) => void;
  resetStreamingDraft: (dealId: string) => void;
  clearThread: (dealId: string) => Promise<void>;
}

export const useConversationStore = create<ConversationState>((set, get) => ({
  byDealId: {},
  streamingDraft: {},
  isStreaming: {},

  async load(_dealId) {
    // bundle loader fills via setMessages
  },

  setMessages(dealId, msgs) {
    set(s => ({ byDealId: { ...s.byDealId, [dealId]: msgs } }));
  },

  async appendMessage(msg) {
    const cur = get().byDealId[msg.dealId] ?? [];
    const next = [...cur, msg];
    set(s => ({ byDealId: { ...s.byDealId, [msg.dealId]: next } }));
    try {
      await conversationRepo.append(msg);
    } catch (e) {
      console.error('Failed to persist message', e);
    }
  },

  appendMessageLocal(msg) {
    const cur = get().byDealId[msg.dealId] ?? [];
    set(s => ({ byDealId: { ...s.byDealId, [msg.dealId]: [...cur, msg] } }));
  },

  setStreaming(dealId, on) {
    set(s => ({ isStreaming: { ...s.isStreaming, [dealId]: on } }));
  },

  appendStreamingDelta(dealId, delta) {
    set(s => ({
      streamingDraft: {
        ...s.streamingDraft,
        [dealId]: (s.streamingDraft[dealId] ?? '') + delta,
      },
    }));
  },

  resetStreamingDraft(dealId) {
    set(s => ({ streamingDraft: { ...s.streamingDraft, [dealId]: '' } }));
  },

  async clearThread(dealId) {
    await conversationRepo.save(dealId, []);
    set(s => ({ byDealId: { ...s.byDealId, [dealId]: [] } }));
  },
}));
